import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPackageById, createPackage, updatePackage, createItineraryDay, updateItineraryDay, deleteItineraryDay, bulkUpdateItinerary } from '../../services/packagesService';
import { getDestinations } from '../../services/locationsService';
import { getGlobalInclusions, getGlobalExclusions } from '../../services/globalListsService';
import { uploadMediaFiles } from '../../services/mediaService';
import { useToast } from '../../contexts/ToastContext';
import Loader from '../../components/common/Loader';
import Input from '../../components/common/Input';
import RichTextEditor from '../../components/forms/RichTextEditor'; // This component needs to be enhanced
import FileUploader from '../../components/forms/FileUploader';
import Button from '../../components/common/Button';
import ItineraryDayInput from '../../components/packages/ItineraryDayInput';
import EnhancedMediaUploader from '../../components/packages/EnhancedMediaUploader';
import { slugify, generateUUID } from '../../utils/helpers';

function PackageFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPackageId, setCurrentPackageId] = useState(id || null); // Track current package ID
  const [formData, setFormData] = useState({
    title: '', slug: '', description: '', note: '',
    images: [], // Full image objects for FileUploader
    imageIds: [], // Image IDs for API calls
    nights: 1, days: 2, // Default to 1 night, 2 days to avoid 0/0 validation errors
    meta_title: '', meta_description: '', meta_keywords: '',
    status: 'draft', is_featured: false, display_order: 0,
    locations: [],
    itinerary: [],
    including: [], // New field for inclusions
    excluding: [] // New field for exclusions
  });
  const [allDestinations, setAllDestinations] = useState([]);
  const [currentItineraryDays, setCurrentItineraryDays] = useState(0);
  const [inclusionOptions, setInclusionOptions] = useState([]);
  const [exclusionOptions, setExclusionOptions] = useState([]);
  
  // Search states for inclusions and exclusions
  const [inclusionSearchTerm, setInclusionSearchTerm] = useState('');
  const [exclusionSearchTerm, setExclusionSearchTerm] = useState('');
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Cache status tracking
  const [cacheStatus, setCacheStatus] = useState({
    destinations: 'loading',
    inclusions: 'loading', 
    exclusions: 'loading'
  });

  // Caching constants
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  const DESTINATIONS_CACHE_KEY = 'package_form_destinations_cache';
  const INCLUSIONS_CACHE_KEY = 'package_form_inclusions_cache';
  const EXCLUSIONS_CACHE_KEY = 'package_form_exclusions_cache';

  // Caching utility functions
  const getCachedData = (cacheKey) => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          return data;
        }
        localStorage.removeItem(cacheKey);
      }
    } catch (error) {
      console.warn(`Failed to read cache for ${cacheKey}:`, error);
      localStorage.removeItem(cacheKey);
    }
    return null;
  };

  const setCachedData = (cacheKey, data) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn(`Failed to cache data for ${cacheKey}:`, error);
    }
  };

  const clearFormDataCaches = () => {
    localStorage.removeItem(DESTINATIONS_CACHE_KEY);
    localStorage.removeItem(INCLUSIONS_CACHE_KEY);
    localStorage.removeItem(EXCLUSIONS_CACHE_KEY);
    showInfo('Form data cache cleared - fresh data will be loaded');
  };

  // Load destinations with caching
  const loadDestinationsIfNeeded = async () => {
    const cached = getCachedData(DESTINATIONS_CACHE_KEY);
    if (cached && Array.isArray(cached) && cached.length > 0) {
      console.log('📋 Using cached destinations:', cached.length, 'items');
      setAllDestinations(cached);
      setCacheStatus(prev => ({ ...prev, destinations: 'cached' }));
      return cached;
    }

    console.log('🔄 Fetching fresh destinations from API...');
    setCacheStatus(prev => ({ ...prev, destinations: 'loading' }));
    const destResponse = await getDestinations();
    const destData = destResponse?.data || destResponse || [];
    
    if (Array.isArray(destData)) {
      setCachedData(DESTINATIONS_CACHE_KEY, destData);
      setAllDestinations(destData);
      setCacheStatus(prev => ({ ...prev, destinations: 'fresh' }));
      console.log('✅ Destinations loaded and cached:', destData.length, 'items');
      return destData;
    } else {
      console.warn('⚠️ Destinations data is not an array:', typeof destData);
      setAllDestinations([]);
      setCacheStatus(prev => ({ ...prev, destinations: 'error' }));
      return [];
    }
  };

  // Load inclusions with caching
  const loadInclusionsIfNeeded = async () => {
    const cached = getCachedData(INCLUSIONS_CACHE_KEY);
    if (cached && Array.isArray(cached) && cached.length > 0) {
      console.log('📋 Using cached inclusions:', cached.length, 'items');
      setInclusionOptions(cached);
      setCacheStatus(prev => ({ ...prev, inclusions: 'cached' }));
      return cached;
    }

    console.log('🔄 Fetching fresh inclusions from API...');
    setCacheStatus(prev => ({ ...prev, inclusions: 'loading' }));
    const inclusionsResponse = await getGlobalInclusions({}, 1, 100);
    const inclusionsList = inclusionsResponse?.data || [];
    
    if (Array.isArray(inclusionsList)) {
      setCachedData(INCLUSIONS_CACHE_KEY, inclusionsList);
      setInclusionOptions(inclusionsList);
      setCacheStatus(prev => ({ ...prev, inclusions: 'fresh' }));
      console.log('✅ Inclusions loaded and cached:', inclusionsList.length, 'items');
      return inclusionsList;
    } else {
      setInclusionOptions([]);
      setCacheStatus(prev => ({ ...prev, inclusions: 'error' }));
      return [];
    }
  };

  // Load exclusions with caching
  const loadExclusionsIfNeeded = async () => {
    const cached = getCachedData(EXCLUSIONS_CACHE_KEY);
    if (cached && Array.isArray(cached) && cached.length > 0) {
      console.log('📋 Using cached exclusions:', cached.length, 'items');
      setExclusionOptions(cached);
      setCacheStatus(prev => ({ ...prev, exclusions: 'cached' }));
      return cached;
    }

    console.log('🔄 Fetching fresh exclusions from API...');
    setCacheStatus(prev => ({ ...prev, exclusions: 'loading' }));
    const exclusionsResponse = await getGlobalExclusions({}, 1, 100);
    const exclusionsList = exclusionsResponse?.data || [];
    
    if (Array.isArray(exclusionsList)) {
      setCachedData(EXCLUSIONS_CACHE_KEY, exclusionsList);
      setExclusionOptions(exclusionsList);
      setCacheStatus(prev => ({ ...prev, exclusions: 'fresh' }));
      console.log('✅ Exclusions loaded and cached:', exclusionsList.length, 'items');
      return exclusionsList;
    } else {
      setExclusionOptions([]);
      setCacheStatus(prev => ({ ...prev, exclusions: 'error' }));
      return [];
    }
  };

  // Manual refresh functions
  const refreshDestinations = async () => {
    localStorage.removeItem(DESTINATIONS_CACHE_KEY);
    setCacheStatus(prev => ({ ...prev, destinations: 'loading' }));
    await loadDestinationsIfNeeded();
    showSuccess('Destinations refreshed successfully');
  };

  const refreshInclusions = async () => {
    localStorage.removeItem(INCLUSIONS_CACHE_KEY);
    setCacheStatus(prev => ({ ...prev, inclusions: 'loading' }));
    await loadInclusionsIfNeeded();
    showSuccess('Inclusions refreshed successfully');
  };

  const refreshExclusions = async () => {
    localStorage.removeItem(EXCLUSIONS_CACHE_KEY);
    setCacheStatus(prev => ({ ...prev, exclusions: 'loading' }));
    await loadExclusionsIfNeeded();
    showSuccess('Exclusions refreshed successfully');
  };

  const refreshAllFormData = async () => {
    clearFormDataCaches();
    setLoading(true);
    try {
      await Promise.all([
        loadDestinationsIfNeeded(),
        loadInclusionsIfNeeded(),
        loadExclusionsIfNeeded()
      ]);
      showSuccess('All form data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing form data:', error);
      showError('Failed to refresh some form data');
    } finally {
      setLoading(false);
    }
  };

  // Cache status indicator component
  const CacheStatusIndicator = ({ status, itemCount }) => {
    const getStatusConfig = () => {
      switch (status) {
        case 'cached':
          return {
            color: 'text-green-600 bg-green-100 border-green-200',
            icon: '💾',
            text: `Cached (${itemCount} items)`
          };
        case 'fresh':
          return {
            color: 'text-blue-600 bg-blue-100 border-blue-200',
            icon: '🔄',
            text: `Fresh (${itemCount} items)`
          };
        case 'loading':
          return {
            color: 'text-gray-600 bg-gray-100 border-gray-200',
            icon: '⏳',
            text: 'Loading...'
          };
        case 'error':
          return {
            color: 'text-red-600 bg-red-100 border-red-200',
            icon: '❌',
            text: 'Error loading'
          };
        default:
          return {
            color: 'text-gray-600 bg-gray-100 border-gray-200',
            icon: '❓',
            text: 'Unknown'
          };
      }
    };

    const config = getStatusConfig();
    
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        <span>{config.text}</span>
      </div>
    );
  };

  // Validation functions
  const validateField = (name, value) => {
    switch (name) {
      case 'title':
        if (!value || value.trim().length < 3) {
          return 'Title must be at least 3 characters long';
        }
        if (value.trim().length > 100) {
          return 'Title must be less than 100 characters';
        }
        return '';
        
      case 'slug':
        if (!value || value.length < 3) {
          return 'Slug must be at least 3 characters long';
        }
        if (!/^[a-z0-9-]+$/.test(value)) {
          return 'Slug can only contain lowercase letters, numbers, and hyphens';
        }
        return '';
        
      case 'nights':
        const nightsNum = parseInt(value) || 0;
        if (nightsNum < 0) {
          return 'Nights cannot be negative';
        }
        if (nightsNum > 365) {
          return 'Nights cannot exceed 365';
        }
        return '';
        
      case 'days':
        const daysNum = parseInt(value) || 0;
        if (daysNum < 1) {
          return 'Days must be at least 1';
        }
        if (daysNum > 366) {
          return 'Days cannot exceed 366';
        }
        const expectedDays = (parseInt(formData.nights) || 0) + 1;
        if (daysNum !== expectedDays) {
          return `Days should be ${expectedDays} (nights + 1)`;
        }
        return '';
        
      case 'meta_title':
        if (value && value.length > 60) {
          return 'Meta title should not exceed 60 characters for SEO';
        }
        return '';
        
      case 'meta_description':
        if (value && value.length > 160) {
          return 'Meta description should not exceed 160 characters for SEO';
        }
        return '';
        
      case 'display_order':
        const orderNum = parseInt(value);
        if (isNaN(orderNum) || orderNum < 0) {
          return 'Display order must be a number >= 0';
        }
        if (orderNum > 9999) {
          return 'Display order cannot exceed 9999';
        }
        return '';
        
      default:
        return '';
    }
  };

  // ValidationMessage component
  const ValidationMessage = ({ error, type = 'error' }) => {
    if (!error) return null;
    
    const styles = {
      error: 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-700',
      warning: 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 text-amber-700', 
      success: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700'
    };
    
    const icons = {
      error: '⚠️',
      warning: '📝', 
      success: '✅'
    };
    
    return (
      <div className={`mt-1 p-2 rounded-lg border text-xs ${styles[type]} flex items-center space-x-2 shadow-sm`}>
        <span>{icons[type]}</span>
        <span>{error}</span>
      </div>
    );
  };

  // Check form validity
  const checkFormValidity = () => {
    const hasTitle = formData.title && formData.title.trim().length >= 3;
    const hasValidDuration = formData.days >= 1 && formData.nights >= 0 && (formData.days === formData.nights + 1);
    const hasImages = formData.images && formData.images.length >= 6;
    const hasNoErrors = Object.values(validationErrors).every(error => error === '');
    
    return hasTitle && hasValidDuration && hasImages && hasNoErrors;
  };

  // Sync currentPackageId with id parameter from URL
  useEffect(() => {
    setCurrentPackageId(id || null);
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Load form data with caching
        await Promise.all([
          loadDestinationsIfNeeded(),
          loadInclusionsIfNeeded(), 
          loadExclusionsIfNeeded()
        ]);

        if (id) {
          const pkg = await getPackageById(id);
          console.log('📦 Package data loaded for editing:', pkg);
          console.log('🔍 Raw package data structure:', {
            hasImageIds: !!pkg.image_ids,
            hasImages: !!pkg.images,
            imageIds: pkg.image_ids,
            images: pkg.images,
            imageIds_type: typeof pkg.image_ids,
            images_type: typeof pkg.images,
            imageIds_length: Array.isArray(pkg.image_ids) ? pkg.image_ids.length : 'N/A',
            images_length: Array.isArray(pkg.images) ? pkg.images.length : 'N/A'
          });
          
          // Transform API data structure to match form structure
          const locations = [];
          
          // Parse destinations if they come as JSON string
          let destinationsData = pkg.destinations;
          if (typeof destinationsData === 'string') {
            try {
              destinationsData = JSON.parse(destinationsData);
            } catch (e) {
              console.warn('Failed to parse destinations JSON:', e);
              destinationsData = null;
            }
          }
          
          // Parse sub_destinations if they come as JSON string
          let subDestinationsData = pkg.sub_destinations;
          if (typeof subDestinationsData === 'string') {
            try {
              subDestinationsData = JSON.parse(subDestinationsData);
            } catch (e) {
              console.warn('Failed to parse sub_destinations JSON:', e);
              subDestinationsData = null;
            }
          }
          
          // Add destinations to locations array (use expanded data if available, otherwise use IDs)
          if (destinationsData && Array.isArray(destinationsData)) {
            destinationsData.forEach(dest => {
              locations.push({
                destination_id: dest.id,
                sub_destination_id: null
              });
            });
          } else if (pkg.destination_ids && Array.isArray(pkg.destination_ids)) {
            pkg.destination_ids.forEach(destId => {
              locations.push({
                destination_id: destId,
                sub_destination_id: null
              });
            });
          }
          
          // Add sub-destinations to locations array (use expanded data if available, otherwise use IDs)
          if (subDestinationsData && Array.isArray(subDestinationsData)) {
            subDestinationsData.forEach(subDest => {
              // Find the parent destination for this sub-destination  
              const parentDest = allDestinations.find(dest => 
                dest.sub_destinations && dest.sub_destinations.some(sub => sub.id === subDest.id)
              );
              
              locations.push({
                destination_id: parentDest ? parentDest.id : null,
                sub_destination_id: subDest.id
              });
            });
          } else if (pkg.sub_destination_ids && Array.isArray(pkg.sub_destination_ids)) {
            pkg.sub_destination_ids.forEach(subDestId => {
              // Find the parent destination for this sub-destination  
              const subDest = allDestinations.find(dest => 
                dest.sub_destinations && dest.sub_destinations.some(sub => sub.id === subDestId)
              );
              
              locations.push({
                destination_id: subDest ? subDest.id : null,
                sub_destination_id: subDestId
              });
            });
          }
          
          // Extract inclusion IDs from the expanded inclusions array or use direct inclusion_ids
          let includingIds = [];
          let inclusionsData = pkg.inclusions;
          
          // Parse JSON string if needed
          if (typeof inclusionsData === 'string') {
            try {
              inclusionsData = JSON.parse(inclusionsData);
            } catch (e) {
              console.warn('Failed to parse inclusions JSON:', e);
              inclusionsData = null;
            }
          }
          
          if (inclusionsData && Array.isArray(inclusionsData)) {
            includingIds = inclusionsData.map(inc => inc.id);
          } else if (pkg.inclusion_ids && Array.isArray(pkg.inclusion_ids)) {
            // Ensure consistent data types (convert to numbers if they're integers)
            includingIds = pkg.inclusion_ids.map(id => typeof id === 'string' && !isNaN(parseInt(id)) ? parseInt(id) : id);
          }
          
          // Extract exclusion IDs from the expanded exclusions array or use direct exclusion_ids
          let excludingIds = [];
          let exclusionsData = pkg.exclusions;
          
          // Parse JSON string if needed
          if (typeof exclusionsData === 'string') {
            try {
              exclusionsData = JSON.parse(exclusionsData);
            } catch (e) {
              console.warn('Failed to parse exclusions JSON:', e);
              exclusionsData = null;
            }
          }
          
          if (exclusionsData && Array.isArray(exclusionsData)) {
            excludingIds = exclusionsData.map(exc => exc.id);
          } else if (pkg.exclusion_ids && Array.isArray(pkg.exclusion_ids)) {
            // Ensure consistent data types (convert to numbers if they're integers)
            excludingIds = pkg.exclusion_ids.map(id => typeof id === 'string' && !isNaN(parseInt(id)) ? parseInt(id) : id);
          }
          
          // Process image data from backend - ensure proper format for FileUploader
          let imageData = [];
          let imageIds = [];
          
          console.log('📷 Raw backend image data:', {
            pkg_images: pkg.images,
            pkg_image_ids: pkg.image_ids,
            images_type: typeof pkg.images,
            images_is_array: Array.isArray(pkg.images),
            images_null: pkg.images === null,
            images_length: pkg.images ? pkg.images.length : 'null',
            image_ids_type: typeof pkg.image_ids,
            image_ids_is_array: Array.isArray(pkg.image_ids),
            image_ids_null: pkg.image_ids === null,
            image_ids_length: pkg.image_ids ? pkg.image_ids.length : 'null',
            full_package_data: pkg
          });
          
          // Handle images from backend response
          if (pkg.images && Array.isArray(pkg.images) && pkg.images.length > 0) {
            // Backend returns full image objects with URLs - use them directly
            imageData = pkg.images.map(img => ({
              id: img.id,
              url: img.url || img.storage_path || `/media/${img.id}`, // Ensure URL exists
              thumbnail_url: img.thumbnail_url || img.thumbnail_path,
              alt_text: img.alt_text || '',
              caption: img.caption || '',
              file_size_bytes: img.file_size_bytes,
              width: img.width,
              height: img.height,
              original_filename: img.original_filename || img.filename || 'Unknown'
            }));
            imageIds = pkg.images.map(img => img.id);
            
            console.log('✅ Using full image objects from backend - found', imageData.length, 'images');
            
          } else if (pkg.image_ids && Array.isArray(pkg.image_ids) && pkg.image_ids.length > 0) {
            // Fallback: backend returned only IDs - create minimal objects
            imageIds = pkg.image_ids;
            imageData = pkg.image_ids.map((id, index) => ({
              id,
              url: `/media/${id}`, // Construct URL from ID
              thumbnail_url: null,
              alt_text: '',
              caption: '',
              original_filename: `Image ${index + 1}`,
              placeholder: true
            }));
            
            console.log('⚠️ Using fallback image objects from IDs only - found', imageData.length, 'image IDs');
            
          } else {
            // No images at all
            console.log('ℹ️ No images found for this package');
            imageData = [];
            imageIds = [];
          }
          
          console.log('📷 Processed image data for form:', {
            imageData_count: imageData.length,
            imageIds_count: imageIds.length,
            imageData_sample: imageData.length > 0 ? {
              id: imageData[0].id,
              url: imageData[0].url,
              hasPlaceholder: !!imageData[0].placeholder
            } : null,
            imageIds_sample: imageIds.length > 0 ? imageIds[0] : null
          });
          
          // Parse itinerary if it comes as JSON string
          let itineraryData = pkg.itinerary;
          if (typeof itineraryData === 'string') {
            try {
              itineraryData = JSON.parse(itineraryData);
            } catch (e) {
              console.warn('Failed to parse itinerary JSON:', e);
              itineraryData = [];
            }
          }
          
          console.log('📋 Processed data for form:', {
            locations: locations.length,
            includingIds: includingIds,
            excludingIds: excludingIds,
            imageIds: imageIds.length,
            itinerary: Array.isArray(itineraryData) ? itineraryData.length : 0,
            destinationsData: destinationsData,
            subDestinationsData: subDestinationsData,
            inclusionsData: inclusionsData,
            exclusionsData: exclusionsData
          });
          
          console.log('🔍 Type checking for form data:', {
            includingIds_types: includingIds.map(id => ({ id, type: typeof id })),
            excludingIds_types: excludingIds.map(id => ({ id, type: typeof id })),
            raw_inclusion_ids: pkg.inclusion_ids,
            raw_exclusion_ids: pkg.exclusion_ids,
            raw_itinerary: pkg.itinerary,
            parsed_itinerary: itineraryData,
            parsed_inclusions: inclusionsData,
            parsed_exclusions: exclusionsData
          });
          
          console.log('🔍 Package description data:', {
            description: pkg.description,
            description_type: typeof pkg.description,
            description_length: pkg.description ? pkg.description.length : 0,
            description_preview: pkg.description ? pkg.description.substring(0, 200) + '...' : 'null'
          });
          
          setFormData({
            ...pkg,
            nights: pkg.nights || 0,
            days: pkg.days || 0,
            display_order: pkg.display_order || 0,
            locations: locations,
            itinerary: Array.isArray(itineraryData) ? itineraryData : [],
            images: imageData, // Use full image objects for FileUploader
            imageIds: imageIds, // Keep IDs separately for API calls
            note: pkg.note || '',
            including: includingIds,
            excluding: excludingIds,
            description: pkg.description || '' // Ensure description is properly set
          });
          
          // Set currentItineraryDays AFTER setting formData to avoid useEffect conflicts
          setCurrentItineraryDays(pkg.days || 0);
        }
      } catch (err) {
        setError('Failed to load data: ' + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (formData.days !== currentItineraryDays && currentItineraryDays !== 0) {
      console.log('🔄 Updating itinerary structure:', {
        currentDays: formData.days,
        previousDays: currentItineraryDays,
        existingItinerary: formData.itinerary
      });
      
      const newItinerary = [];
      for (let i = 1; i <= formData.days; i++) {
        const existingDay = formData.itinerary.find(day => day.day_number === i);
        if (existingDay) {
          // Preserve existing data completely
          newItinerary.push({ ...existingDay });
          console.log(`📋 Preserving existing day ${i}:`, existingDay);
        } else {
          // Only create empty objects for truly new days
          const newDay = {
            day_number: i,
            title: '',
            description: ''
          };
          newItinerary.push(newDay);
          console.log(`📋 Creating new empty day ${i}:`, newDay);
        }
      }
      
      console.log('📋 Final new itinerary structure:', newItinerary);
      
      // Always update to ensure proper array length
      setFormData(prev => ({ ...prev, itinerary: newItinerary }));
      setCurrentItineraryDays(formData.days);
    }
  }, [formData.days, currentItineraryDays]); // Removed formData.itinerary from deps to prevent infinite loops

  // Monitor form validity
  useEffect(() => {
    setIsFormValid(checkFormValidity());
  }, [formData, validationErrors]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'nights') {
      const nightsValue = parseInt(value) || 0;
      const daysValue = nightsValue + 1;
      
      setFormData(prev => ({
        ...prev,
        nights: nightsValue,
        days: daysValue,
        ...(formData.title && { slug: slugify(formData.title) })
      }));
      
      // Validate nights and days
      const nightsError = validateField('nights', nightsValue);
      const daysError = validateField('days', daysValue);
      setValidationErrors(prev => ({
        ...prev,
        nights: nightsError,
        days: daysError
      }));
      
      // Show helpful info about automatic days calculation
      if (nightsValue > 0) {
        showInfo(`Days automatically set to ${daysValue} (${nightsValue} nights + 1)`, 3000);
      }
    } else if (name === 'days') {
      const daysValue = parseInt(value) || 0;
      const nightsValue = Math.max(0, daysValue - 1);
      
      setFormData(prev => ({
        ...prev,
        days: daysValue,
        nights: nightsValue,
        ...(formData.title && { slug: slugify(formData.title) })
      }));
      
      // Validate days and nights
      const daysError = validateField('days', daysValue);
      const nightsError = validateField('nights', nightsValue);
      setValidationErrors(prev => ({
        ...prev,
        days: daysError,
        nights: nightsError
      }));
      
      // Show helpful info about automatic nights calculation
      if (daysValue > 0) {
        showInfo(`Nights automatically set to ${nightsValue} (${daysValue} days - 1)`, 3000);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
        ...(name === 'title' && { slug: slugify(value) })
      }));
      
      // Real-time validation for other fields
      if (name === 'title') {
        const titleError = validateField('title', value);
        const slugError = validateField('slug', slugify(value));
        setValidationErrors(prev => ({
          ...prev,
          title: titleError,
          slug: slugError
        }));
      } else {
        const error = validateField(name, value);
        setValidationErrors(prev => ({
          ...prev,
          [name]: error
        }));
      }
    }
  };

  const handleImagesUpload = async (files) => {
    try {
      console.log('🔄 Starting image upload for', files.length, 'files');
      showInfo('Uploading images...', 2000);
      
      // Upload files to media service
      const uploadResult = await uploadMediaFiles(files, 'packages');
      
      if (uploadResult.success && uploadResult.uploadedFiles.length > 0) {
        console.log('✅ Upload result:', uploadResult);
        
        // Create image objects from uploaded files
        const newImageObjects = uploadResult.uploadedFiles.map(file => ({
          id: file.id,
          url: file.url || file.storage_path,
          thumbnail_url: file.thumbnail_url || file.thumbnail_path,
          alt_text: file.alt_text,
          caption: file.caption,
          file_size_bytes: file.file_size_bytes,
          width: file.width,
          height: file.height
        }));
        
        // Extract just the IDs
        const newImageIds = uploadResult.uploadedFiles.map(file => file.id);
        
        console.log('✅ New image objects:', newImageObjects);
        console.log('✅ New image IDs:', newImageIds);
        
        // Update form state with both image objects and IDs
        setFormData(prev => {
          const updatedImages = [...(prev.images || []), ...newImageObjects];
          const updatedImageIds = [...(prev.imageIds || []), ...newImageIds];
          
          console.log('📷 Updated images:', updatedImages);
          console.log('📷 Updated image IDs:', updatedImageIds);
          
          return {
            ...prev, 
            images: updatedImages, // For FileUploader display
            imageIds: updatedImageIds // For API calls
          };
        });
        
        showSuccess(`Successfully uploaded ${uploadResult.uploadedFiles.length} images!`);
        
        // Show warnings for failed uploads
        if (uploadResult.failedFiles.length > 0) {
          const failedNames = uploadResult.failedFiles.map(f => f.filename).join(', ');
          showWarning(`Failed to upload: ${failedNames}`);
        }
      } else {
        throw new Error(uploadResult.message || 'Upload failed');
      }
    } catch (error) {
      console.error('❌ Image upload error:', error);
      showError(`Image upload failed: ${error.message}`);
    }
  };

  const handleImageRemove = (imageId) => {
    console.log('🗑️ Removing image:', imageId);
    
    setFormData(prev => {
      const updatedImages = prev.images.filter(img => img.id !== imageId);
      const updatedImageIds = prev.imageIds.filter(id => id !== imageId);
      
      console.log('📷 Images after removal:', updatedImages);
      console.log('📷 Image IDs after removal:', updatedImageIds);
      
      return {
        ...prev,
        images: updatedImages,
        imageIds: updatedImageIds
      };
    });
    
    showInfo('Image removed from package');
  };

  // Enhanced media uploader handler
  const handleEnhancedMediaChange = async (files) => {
    console.log('🔄 Enhanced media change:', files);
    
    try {
      // Check if this is just a reordering operation (same files, different order)
      const currentImageIds = formData.imageIds || [];
      const newImageIds = files.map(file => file.id).filter(id => id);
      
      const isReordering = files.length === formData.images.length && 
                          files.every(file => file.id) && 
                          newImageIds.length === currentImageIds.length &&
                          newImageIds.every(id => currentImageIds.includes(id)) &&
                          currentImageIds.every(id => newImageIds.includes(id));
      
      if (isReordering) {
        // Just update the order, don't process uploads
        console.log('🔄 Reordering existing images');
        setFormData(prev => ({
          ...prev,
          images: files,
          imageIds: files.map(file => file.id)
        }));
        return;
      }
      
      // Original logic for new uploads/selections
      // Separate local files from media library files
      const localFiles = files.filter(file => file.file || (!file.id && file.name));
      const mediaLibraryFiles = files.filter(file => file.id && file.isFromMediaLibrary);
      
      console.log('📁 Local files to upload:', localFiles.length);
      console.log('📚 Media library files:', mediaLibraryFiles.length);
      
      let allImageObjects = [];
      let allImageIds = [];
      
      // Handle local file uploads
      if (localFiles.length > 0) {
        showInfo('Uploading local files...', 2000);
        
        const filesToUpload = localFiles.map(file => file.file || file);
        const uploadResult = await uploadMediaFiles(filesToUpload, 'packages');
        
        if (uploadResult.success && uploadResult.uploadedFiles.length > 0) {
          const uploadedImageObjects = uploadResult.uploadedFiles.map(file => ({
            id: file.id,
            url: file.url || file.storage_path,
            thumbnail_url: file.thumbnail_url || file.thumbnail_path,
            alt_text: file.alt_text,
            caption: file.caption,
            file_size_bytes: file.file_size_bytes,
            width: file.width,
            height: file.height,
            original_filename: file.original_filename
          }));
          
          const uploadedImageIds = uploadResult.uploadedFiles.map(file => file.id);
          
          allImageObjects.push(...uploadedImageObjects);
          allImageIds.push(...uploadedImageIds);
          
          showSuccess(`Successfully uploaded ${uploadResult.uploadedFiles.length} new images!`);
        }
      }
      
      // Handle media library selections (these are already uploaded)
      if (mediaLibraryFiles.length > 0) {
        const libraryImageObjects = mediaLibraryFiles.map(file => ({
          id: file.id,
          url: file.url,
          thumbnail_url: file.thumbnail_url,
          alt_text: file.alt_text,
          caption: file.caption,
          file_size_bytes: file.file_size_bytes,
          width: file.width,
          height: file.height,
          original_filename: file.original_filename
        }));
        
        const libraryImageIds = mediaLibraryFiles.map(file => file.id);
        
        allImageObjects.push(...libraryImageObjects);
        allImageIds.push(...libraryImageIds);
        
        showInfo(`Selected ${mediaLibraryFiles.length} images from media library`);
      }
      
      // Update form state
      setFormData(prev => ({
        ...prev, 
        images: allImageObjects, 
        imageIds: allImageIds
      }));
      
      console.log('✅ Final image objects:', allImageObjects);
      console.log('✅ Final image IDs:', allImageIds);
      
    } catch (error) {
      console.error('❌ Enhanced media change error:', error);
      showError(`Media update failed: ${error.message}`);
    }
  };

  const handleItineraryDayChange = (dayNumber, dayData) => {
    console.log('📋 Itinerary day changed:', { dayNumber, dayData });
    setFormData(prev => {
      const newItinerary = [...prev.itinerary];
      
      // Ensure the array has enough elements
      while (newItinerary.length < dayNumber) {
        newItinerary.push({
          day_number: newItinerary.length + 1,
          title: '',
          description: ''
        });
      }
      
      // Update the specific day
      const dayIndex = dayNumber - 1;
      newItinerary[dayIndex] = { ...dayData, day_number: dayNumber };
      
      console.log('📋 Updated itinerary array:', newItinerary);
      
      return {
        ...prev,
        itinerary: newItinerary
      };
    });
  };

  const handleDeleteItineraryDay = async (dayNumber) => {
    console.log('🗑️ Deleting itinerary day:', dayNumber);
    
    try {
      // Show confirmation
      const confirmed = window.confirm(`Are you sure you want to delete Day ${dayNumber}? This action cannot be undone.`);
      if (!confirmed) return;

      // If this is an edit mode and the package exists, delete from backend
      if (id) {
        try {
          await deleteItineraryDay(id, dayNumber);
          showSuccess(`Day ${dayNumber} deleted successfully`);
        } catch (error) {
          // If it fails with 404, the day wasn't in the database yet, which is fine
          if (error.response?.status !== 404) {
            console.error('❌ Failed to delete from backend:', error);
            showError(`Failed to delete Day ${dayNumber}: ${error.message}`);
            return;
          }
        }
      }

      // Remove from frontend state
      setFormData(prev => {
        const newItinerary = prev.itinerary.filter(day => day.day_number !== dayNumber);
        
        // Renumber the remaining days to fill gaps
        const renumberedItinerary = newItinerary.map((day, index) => ({
          ...day,
          day_number: index + 1
        }));
        
        console.log('📋 Itinerary after deletion and renumbering:', renumberedItinerary);
        
        // Also adjust the days count
        const newDaysCount = Math.max(1, prev.days - 1);
        const newNightsCount = Math.max(0, newDaysCount - 1);
        
        showInfo(`Package adjusted to ${newDaysCount} days / ${newNightsCount} nights`);
        
        return {
          ...prev,
          itinerary: renumberedItinerary,
          days: newDaysCount,
          nights: newNightsCount
        };
      });

      // Update currentItineraryDays to trigger useEffect
      setCurrentItineraryDays(prev => Math.max(1, prev - 1));
      
    } catch (error) {
      console.error('❌ Error deleting itinerary day:', error);
      showError(`Failed to delete Day ${dayNumber}: ${error.message}`);
    }
  };

  const handleAddItineraryDay = () => {
    console.log('➕ Adding new itinerary day');
    
    setFormData(prev => {
      const newDaysCount = prev.days + 1;
      const newNightsCount = newDaysCount - 1;
      
      const newDay = {
        day_number: newDaysCount,
        title: '',
        description: ''
      };
      
      const newItinerary = [...prev.itinerary, newDay];
      
      console.log('📋 Added new day:', newDay);
      console.log('📋 New itinerary:', newItinerary);
      
      showInfo(`Added Day ${newDaysCount} - Package is now ${newDaysCount} days / ${newNightsCount} nights`);
      
      return {
        ...prev,
        itinerary: newItinerary,
        days: newDaysCount,
        nights: newNightsCount
      };
    });

    // Update currentItineraryDays to trigger useEffect
    setCurrentItineraryDays(prev => prev + 1);
  };

  const handleLocationChange = (type, id, parentDestinationId = null) => {
    setFormData(prev => {
      let updatedLocations = [...prev.locations];

      const isCurrentlySelected = updatedLocations.some(loc =>
        (type === 'destination' && loc.destination_id === id && loc.sub_destination_id === null) ||
        (type === 'sub_destination' && loc.sub_destination_id === id)
      );

      if (isCurrentlySelected) {
        updatedLocations = updatedLocations.filter(loc =>
          !((type === 'destination' && loc.destination_id === id && loc.sub_destination_id === null) ||
            (type === 'sub_destination' && loc.sub_destination_id === id))
        );
      } else {
        if (type === 'destination') {
          updatedLocations.push({ destination_id: id, sub_destination_id: null });
        } else if (type === 'sub_destination') {
          const parentDestSelectedAsMain = updatedLocations.some(loc =>
            loc.destination_id === parentDestinationId && loc.sub_destination_id === null
          );
          if (!parentDestSelectedAsMain && parentDestinationId) {
            updatedLocations.push({ destination_id: parentDestinationId, sub_destination_id: null });
          }
          updatedLocations.push({ destination_id: parentDestinationId, sub_destination_id: id });
        }
      }
      return { ...prev, locations: updatedLocations };
    });
  };

  const handleInclusionChange = (inclusion) => {
    console.log('🔍 Inclusion selected:', inclusion, 'ID type:', typeof inclusion.id);
    setFormData(prev => ({
      ...prev,
      including: prev.including.includes(inclusion.id)
        ? prev.including.filter(item => item !== inclusion.id)
        : [...prev.including, inclusion.id]
    }));
  };

  const handleExclusionChange = (exclusion) => {
    console.log('🔍 Exclusion selected:', exclusion, 'ID type:', typeof exclusion.id);
    setFormData(prev => ({
      ...prev,
      excluding: prev.excluding.includes(exclusion.id)
        ? prev.excluding.filter(item => item !== exclusion.id)
        : [...prev.excluding, exclusion.id]
    }));
  };

  // Filter inclusions based on search term
  const filteredInclusions = inclusionOptions.filter(inclusion =>
    inclusion.inclusion_text && 
    inclusion.inclusion_text.toLowerCase().includes(inclusionSearchTerm.toLowerCase())
  );

  // Filter exclusions based on search term
  const filteredExclusions = exclusionOptions.filter(exclusion =>
    exclusion.exclusion_text && 
    exclusion.exclusion_text.toLowerCase().includes(exclusionSearchTerm.toLowerCase())
  );

  const isLocationSelected = (locationId, type) => {
    const isSelected = formData.locations.some(loc =>
      (type === 'destination' && loc.destination_id === locationId && loc.sub_destination_id === null) ||
      (type === 'sub_destination' && loc.sub_destination_id === locationId)
    );
    
    if (isSelected) {
      console.log('🔍 Location selected:', {
        locationId,
        type,
        formData_locations: formData.locations,
        isSelected
      });
    }
    
    return isSelected;
  };

  const handleItineraryUpdates = async (packageId) => {
    console.log('📋 Handling itinerary updates for package:', packageId);
    console.log('📋 Current itinerary data:', formData.itinerary);
    
    try {
      // Filter out days with content and prepare for bulk update
      const daysWithContent = formData.itinerary.filter(day => 
        day && day.day_number && (day.title || day.description)
      );
      
      console.log('📋 Days with content for bulk update:', daysWithContent);
      
      if (daysWithContent.length > 0) {
        // Use bulk update API for better performance and atomicity
        const result = await bulkUpdateItinerary(packageId, daysWithContent);
        console.log('✅ Bulk itinerary update completed:', result);
        
        if (result.data && result.data.total_days > 0) {
          showSuccess(`Itinerary updated successfully - ${result.data.total_days} days saved`);
        } else {
          showInfo('Itinerary updated - no days with content to save');
        }
      } else {
        // No content days - clear all itinerary
        await bulkUpdateItinerary(packageId, []);
        console.log('✅ Cleared all itinerary days (no content)');
        showInfo('Itinerary cleared - no days with content');
      }
      
    } catch (error) {
      console.error('❌ Error handling itinerary updates:', error);
      showWarning('Package updated but some itinerary changes may not have been saved');
      
      // Fallback to individual updates if bulk update fails
      console.log('🔄 Falling back to individual itinerary updates...');
      await handleIndividualItineraryUpdates(packageId);
    }
  };

  // Fallback method for individual itinerary updates
  const handleIndividualItineraryUpdates = async (packageId) => {
    let successfulUpdates = 0;
    let failedUpdates = 0;
    
    try {
      // Process each day in the current itinerary
      for (const day of formData.itinerary) {
        // Ensure day has proper structure
        if (!day || !day.day_number) {
          console.warn('⚠️ Skipping invalid day:', day);
          continue;
        }
        
        if (day.title || day.description) {
          // Day has content, create or update it
          try {
            const dayPayload = {
              title: day.title || '',
              description: day.description || ''
            };
            
            console.log(`📋 Processing day ${day.day_number} with payload:`, dayPayload);
            
            // Try to update first (assumes it might exist)
            await updateItineraryDay(packageId, day.day_number, dayPayload);
            console.log(`✅ Updated itinerary day ${day.day_number}`);
            successfulUpdates++;
          } catch (updateError) {
            // If update fails (day doesn't exist), try to create it
            if (updateError.response?.status === 404) {
              try {
                const createPayload = {
                  day_number: day.day_number,
                  title: day.title || '',
                  description: day.description || ''
                };
                
                console.log(`📋 Creating day ${day.day_number} with payload:`, createPayload);
                await createItineraryDay(packageId, createPayload);
                console.log(`✅ Created itinerary day ${day.day_number}`);
                successfulUpdates++;
              } catch (createError) {
                console.error(`❌ Failed to create itinerary day ${day.day_number}:`, createError);
                failedUpdates++;
              }
            } else {
              console.error(`❌ Failed to update itinerary day ${day.day_number}:`, updateError);
              failedUpdates++;
            }
          }
        } else {
          // Day has no content, try to delete it if it exists
          try {
            await deleteItineraryDay(packageId, day.day_number);
            console.log(`✅ Deleted empty itinerary day ${day.day_number}`);
          } catch (deleteError) {
            // Ignore 404 errors for deletion (day didn't exist anyway)
            if (deleteError.response?.status !== 404) {
              console.error(`❌ Failed to delete itinerary day ${day.day_number}:`, deleteError);
            }
          }
        }
      }
      
      // Show appropriate success message
      if (successfulUpdates > 0 && failedUpdates === 0) {
        console.log(`✅ Successfully updated ${successfulUpdates} itinerary days`);
      } else if (failedUpdates > 0) {
        showWarning(`Updated package but ${failedUpdates} itinerary days failed to save`);
      }
      
    } catch (error) {
      console.error('❌ Error in individual itinerary updates:', error);
      showWarning('Package updated but some itinerary changes may not have been saved');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Client-side validation with toast notifications
    if (!formData.title || formData.title.trim() === '') {
      showWarning('Please enter a package title');
      return;
    }
    
    if (formData.days < 1) {
      showWarning('Days must be at least 1');
      return;
    }
    
    if (formData.nights < 0) {
      showWarning('Nights cannot be negative');
      return;
    }
    
    if (formData.days !== formData.nights + 1) {
      showWarning(`Days must equal nights + 1. Current: ${formData.days} days, ${formData.nights} nights. Please adjust the values.`);
      return;
    }
    
    // Additional validation for common issues
    if (formData.days === 0 && formData.nights === 0) {
      showWarning('Please set the number of days and nights for this package');
      return;
    }
    
    setLoading(true);
    
    try {
      // Transform form data to API format
      const destination_ids = [];
      const sub_destination_ids = [];
      
      // Extract destination and sub-destination IDs from locations
      formData.locations.forEach(loc => {
        if (loc.sub_destination_id) {
          sub_destination_ids.push(String(loc.sub_destination_id)); // Ensure string format for UUID
        } else if (loc.destination_id) {
          destination_ids.push(String(loc.destination_id)); // Ensure string format for UUID
        }
      });
      
      const apiData = {
        title: formData.title || '',
        slug: formData.slug || '',
        description: formData.description || '',
        note: formData.note || '',
        nights: parseInt(formData.nights) || 0,
        days: parseInt(formData.days) || 0,
        status: formData.status || 'draft',
        is_featured: Boolean(formData.is_featured),
        display_order: parseInt(formData.display_order) || 0,
        destination_ids: destination_ids,
        sub_destination_ids: sub_destination_ids,
        // Use uploaded image IDs from media service
        image_ids: Array.isArray(formData.imageIds) ? formData.imageIds.map(id => String(id)) : [],
        inclusion_ids: Array.isArray(formData.including) ? formData.including.map(id => String(id)) : [], // Ensure string format for UUID
        exclusion_ids: Array.isArray(formData.excluding) ? formData.excluding.map(id => String(id)) : [], // Ensure string format for UUID
        meta_title: formData.meta_title || '',
        meta_description: formData.meta_description || '',
        meta_keywords: formData.meta_keywords || ''
      };
      
      console.log('📤 Submitting package data:', {
        title: apiData.title,
        slug: apiData.slug,
        nights: apiData.nights,
        days: apiData.days,
        status: apiData.status,
        is_featured: apiData.is_featured,
        display_order: apiData.display_order,
        image_ids_count: apiData.image_ids?.length || 0,
        destination_ids_count: apiData.destination_ids?.length || 0,
        sub_destination_ids_count: apiData.sub_destination_ids?.length || 0,
        inclusion_ids_count: apiData.inclusion_ids?.length || 0,
        exclusion_ids_count: apiData.exclusion_ids?.length || 0,
        meta_fields: {
          meta_title: !!apiData.meta_title,
          meta_description: !!apiData.meta_description,
          meta_keywords: !!apiData.meta_keywords
        }
      });
      
      // Additional validation logging
      console.log('🔍 Full API data being sent:', JSON.stringify(apiData, null, 2));
      console.log('🔍 Data types check:', {
        destination_ids: {
          type: typeof apiData.destination_ids,
          isArray: Array.isArray(apiData.destination_ids),
          sample: apiData.destination_ids[0],
          sampleType: typeof apiData.destination_ids[0]
        },
        inclusion_ids: {
          type: typeof apiData.inclusion_ids,
          isArray: Array.isArray(apiData.inclusion_ids),
          sample: apiData.inclusion_ids[0],
          sampleType: typeof apiData.inclusion_ids[0]
        },
        exclusion_ids: {
          type: typeof apiData.exclusion_ids,
          isArray: Array.isArray(apiData.exclusion_ids),
          sample: apiData.exclusion_ids[0],
          sampleType: typeof apiData.exclusion_ids[0]
        }
      });
      
      if (currentPackageId) {
        await updatePackage(currentPackageId, apiData);
        
        // Handle itinerary updates for existing packages
        await handleItineraryUpdates(currentPackageId);
        
        showSuccess('Package updated successfully!');
        // Don't redirect after update, let user stay on the form
      } else {
        const createdPackage = await createPackage(apiData);
        console.log('✅ Package created:', createdPackage);
        
        // Extract package ID from response
        const packageId = createdPackage?.id || createdPackage?.data?.id;
        
        if (!packageId) {
          console.error('❌ No package ID returned from creation');
          showError('Package created but no ID returned');
          return;
        }
        
        // Create itinerary days separately for new packages
        if (formData.itinerary && formData.itinerary.length > 0) {
          console.log('📋 Creating itinerary days...');
          let successfulDays = 0;
          let failedDays = 0;
          
          for (const day of formData.itinerary) {
            if (day.title || day.description) {  // Only create days with content
              try {
                await createItineraryDay(packageId, day);
                console.log(`✅ Created itinerary day ${day.day_number}`);
                successfulDays++;
              } catch (dayError) {
                console.error(`❌ Failed to create itinerary day ${day.day_number}:`, dayError);
                failedDays++;
              }
            }
          }
          
          if (successfulDays > 0 && failedDays === 0) {
            showSuccess(`Package created successfully with ${successfulDays} itinerary days!`);
          } else if (successfulDays > 0 && failedDays > 0) {
            showWarning(`Package created but ${failedDays} itinerary days failed to save`);
          } else if (failedDays > 0) {
            showWarning('Package created but itinerary days failed to save');
          }
        } else {
          showSuccess('Package created successfully!');
        }
        
        // Switch form to edit mode for the newly created package
        console.log('🔄 Switching to edit mode for newly created package:', packageId);
        
        // Update the component state to track the new package ID
        setCurrentPackageId(packageId);
        
        // Update the URL to reflect edit mode without causing navigation
        window.history.replaceState({}, '', `/packages/${packageId}`);
        
        // Don't redirect automatically - let user manually navigate using "Back to Packages" button
      }
    } catch (err) {
      console.error('❌ Package save error:', err);
      console.error('❌ Full error object:', {
        message: err.message,
        response: err.response,
        request: err.request,
        config: err.config
      });
      
      // Extract meaningful error message with better debugging
      let errorMessage = 'Unknown error occurred';
      let errorDetails = '';
      
      if (err.response) {
        // The request was made and the server responded with a status code
        const { status, data } = err.response;
        console.error('❌ Response error:', { status, data });
        
        if (data) {
          if (typeof data === 'string') {
            errorMessage = data;
          } else if (data.detail) {
            if (Array.isArray(data.detail)) {
              // Pydantic validation errors
              const validationErrors = data.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
              errorMessage = `Validation error: ${validationErrors}`;
            } else if (typeof data.detail === 'string') {
              errorMessage = data.detail;
            } else {
              errorMessage = JSON.stringify(data.detail);
            }
          } else if (data.message) {
            errorMessage = data.message;
          } else if (data.error) {
            errorMessage = data.error;
          } else {
            errorMessage = `Server error (${status}): ${JSON.stringify(data)}`;
          }
        } else {
          errorMessage = `Server error (${status})`;
        }
        
        // Add helpful context for common errors
        if (status === 422) {
          errorDetails = ' Please check your form data and try again.';
        } else if (status === 400) {
          errorDetails = ' Please verify all required fields are filled correctly.';
        } else if (status === 401) {
          errorDetails = ' Please log in again.';
        } else if (status === 403) {
          errorDetails = ' You do not have permission to perform this action.';
        } else if (status >= 500) {
          errorDetails = ' Server error - please try again later.';
        }
      } else if (err.request) {
        // The request was made but no response was received
        console.error('❌ Network error - no response received');
        errorMessage = 'Network error: Could not connect to server. Please check your internet connection.';
      } else if (err.message) {
        // Something happened in setting up the request
        errorMessage = err.message;
      }
      
      const fullErrorMessage = `${errorMessage}${errorDetails}`;
      console.error('❌ Final error message:', fullErrorMessage);
      
      // Show error with appropriate type based on status
      if (err.response?.status === 422 || err.response?.status === 400) {
        showWarning(fullErrorMessage);
      } else {
        showError(fullErrorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl shadow-lg" role="alert">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
            <span className="block sm:inline font-medium">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      <div className="w-full">
        <div className="flex justify-between items-center px-2 mb-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
            {id ? 'Edit Package' : 'Create New Package'}
          </h2>
          <button
            type="button"
            onClick={refreshAllFormData}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh all form data (destinations, inclusions, exclusions)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>🔄 Refresh All Data</span>
          </button>
        </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">

        {/* Row 1: Basic Info + Status */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
            Basic Information
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-2">
              <Input 
                label="Title" 
                id="title" 
                name="title" 
                value={formData.title} 
                onChange={handleInputChange} 
                required 
                className={`border-blue-200 focus:border-blue-500 focus:ring-blue-500 ${validationErrors.title ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter package title (min 3 characters)"
              />
              <ValidationMessage error={validationErrors.title} />
              {formData.title && !validationErrors.title && formData.title.trim().length >= 3 && (
                <ValidationMessage error={`Perfect title! (${formData.title.trim().length} characters)`} type="success" />
              )}
              {formData.title && formData.title.trim().length > 0 && formData.title.trim().length < 3 && (
                <ValidationMessage error={`${3 - formData.title.trim().length} more characters needed`} type="warning" />
              )}
            </div>
            <div>
              <Input 
                label="Slug" 
                id="slug" 
                name="slug" 
                value={formData.slug} 
                onChange={handleInputChange} 
                disabled={!!id} 
                className={`border-blue-200 focus:border-blue-500 focus:ring-blue-500 ${validationErrors.slug ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Auto-generated from title"
              />
              <ValidationMessage error={validationErrors.slug} />
              {formData.slug && !validationErrors.slug && formData.slug.length >= 3 && (
                <ValidationMessage error="Valid URL slug format" type="success" />
              )}
            </div>
            <div>
              <label htmlFor="status" className="block text-gray-700 text-sm font-semibold mb-2">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="shadow-sm border border-blue-200 rounded-lg w-full py-3 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
              {formData.status === 'published' && (
                <ValidationMessage error="Package will be visible to users" type="success" />
              )}
              {formData.status === 'draft' && (
                <ValidationMessage error="Package is in draft mode" type="warning" />
              )}
            </div>
          </div>
          
          {/* Display Order Row */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div>
              <Input 
                label="Display Order" 
                id="display_order" 
                name="display_order" 
                type="number" 
                value={formData.display_order} 
                onChange={handleInputChange} 
                min="0" 
                max="9999"
                className={`border-blue-200 focus:border-blue-500 focus:ring-blue-500 ${validationErrors.display_order ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Display order (0 for default)"
              />
              <ValidationMessage error={validationErrors.display_order} />
              {formData.display_order >= 0 && !validationErrors.display_order && (
                <ValidationMessage error={`Order: ${formData.display_order}`} type="success" />
              )}
              <p className="text-xs text-gray-500 mt-1">
                Lower numbers appear first in listings. 0 = default order.
              </p>
            </div>
          </div>
        </div>

        {/* Row 2: Duration + Featured */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
            Duration & Settings
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div>
              <Input 
                label="Nights" 
                id="nights" 
                name="nights" 
                type="number" 
                value={formData.nights} 
                onChange={handleInputChange} 
                required 
                min="0" 
                max="365"
                className={`border-green-200 focus:border-green-500 focus:ring-green-500 ${validationErrors.nights ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Number of nights"
              />
              <ValidationMessage error={validationErrors.nights} />
              {formData.nights >= 0 && formData.nights <= 365 && !validationErrors.nights && (
                <ValidationMessage error={`${formData.nights} nights selected`} type="success" />
              )}
            </div>
            <div>
              <Input 
                label="Days" 
                id="days" 
                name="days" 
                type="number" 
                value={formData.days} 
                onChange={handleInputChange} 
                required 
                min="1" 
                max="366"
                className={`border-green-200 focus:border-green-500 focus:ring-green-500 ${validationErrors.days ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Number of days"
              />
              <ValidationMessage error={validationErrors.days} />
              {formData.days >= 1 && formData.days <= 366 && !validationErrors.days && (
                <ValidationMessage error={`${formData.days} days selected`} type="success" />
              )}
            </div>
            <div className="flex items-end pb-1">
              <div className="text-sm text-gray-700 bg-white p-3 rounded-lg border border-green-200 shadow-sm">
                <span className="font-medium">Duration:</span> {formData.nights}N/{formData.days}D
                {formData.days !== formData.nights + 1 && (
                  <ValidationMessage error="Days should equal nights + 1" type="warning" />
                )}
                {formData.days === formData.nights + 1 && (
                  <ValidationMessage error="Perfect duration balance!" type="success" />
                )}
              </div>
            </div>
            <div className="flex items-end pb-1">
              <label className="inline-flex items-center bg-white p-3 rounded-lg border border-green-200 shadow-sm hover:bg-green-50 transition-colors duration-200 cursor-pointer">
                <input type="checkbox" name="is_featured" checked={formData.is_featured} onChange={handleInputChange} className="form-checkbox h-5 w-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500" />
                <span className="ml-3 text-gray-700 font-medium flex items-center">
                  Is Featured
                  {formData.is_featured && <div className="w-2 h-2 bg-green-500 rounded-full ml-2"></div>}
                </span>
              </label>
              {formData.is_featured && (
                <ValidationMessage error="Package will appear prominently" type="success" />
              )}
            </div>
          </div>
        </div>

        {/* Row 3: Description */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
            Note
          </h3>
          <RichTextEditor 
            label="Note" 
            value={formData.description} 
            onChange={(val) => {
              setFormData(prev => ({ ...prev, description: val }));
              const error = validateField('description', val);
              setValidationErrors(prev => ({ ...prev, description: error }));
            }}
            className={validationErrors.description ? 'border-red-300' : 'border-purple-200'}
            placeholder="Enter detailed package description (min 20 characters)"
          />
          <ValidationMessage error={validationErrors.description} />
          {formData.description && !validationErrors.description && formData.description.trim().length >= 20 && (
            <ValidationMessage error={`Excellent description! (${formData.description.trim().length} characters)`} type="success" />
          )}
          {formData.description && formData.description.trim().length > 0 && formData.description.trim().length < 20 && (
            <ValidationMessage error={`${20 - formData.description.trim().length} more characters needed`} type="warning" />
          )}
        </div>

        {/* Row 4: Note */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-amber-500 rounded-full mr-3"></div>
            Additional Note
          </h3>
          <RichTextEditor label="Additional Note" value={formData.note} onChange={(val) => setFormData(prev => ({ ...prev, note: val }))} />
        </div>

        {/* Row 5: Images */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-teal-500 rounded-full mr-3"></div>
            Package Images
          </h3>
          
          <EnhancedMediaUploader
            label="Package Images (Min 6 images, Max 10 images)"
            initialFiles={formData.images || []}
            onFilesChange={handleEnhancedMediaChange}
            minFiles={6}
            maxFiles={10}
            category="packages"
            error={validationErrors.images}
          />
        </div>

        {/* Row 6: Itinerary */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
              Daily Itinerary
            </h3>
            <button
              type="button"
              onClick={handleAddItineraryDay}
              className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
              title="Add new day"
            >
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              <span>Add Day</span>
            </button>
          </div>
          <div className="space-y-4">
            {Array.from({ length: formData.days }).map((_, index) => {
              const dayData = formData.itinerary[index] || {
                day_number: index + 1,
                title: '',
                description: ''
              };
              return (
              <div key={index + 1} className="border-2 border-gray-200 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow duration-200 relative">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-gray-800 flex items-center">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></div>
                    Day {index + 1}
                  </h4>
                  {formData.days > 1 && (
                    <button
                      type="button"
                      onClick={() => handleDeleteItineraryDay(index + 1)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                      title={`Delete Day ${index + 1}`}
                    >
                      <svg 
                        className="w-5 h-5" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
               <div className="flex flex-col gap-4">
                  <Input
                    label="Title"
                    value={dayData?.title || ''}
                    onChange={(e) => handleItineraryDayChange(index + 1, {
                      ...dayData,
                      day_number: index + 1,
                      title: e.target.value
                    })}
                    className="w-full border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  <RichTextEditor
                    label="Description"
                    value={dayData?.description || ''}
                    onChange={(val) => handleItineraryDayChange(index + 1, {
                      ...dayData,
                      day_number: index + 1,
                      description: val
                    })}
                    className="w-full border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
              );
            })}
            {formData.days === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-gray-500">Set Days count to add itinerary sections.</p>
              </div>
            )}
          </div>
        </div>

        {/* Row 7: Locations + Inclusions/Exclusions */}
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <div className="w-3 h-3 bg-slate-500 rounded-full mr-3"></div>
            Locations & Package Details
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Locations */}
            <div className="border-2 border-blue-200 rounded-xl p-5 bg-gradient-to-br from-blue-50 to-cyan-50 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-3">
                  <h4 className="text-md font-semibold text-gray-800 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Locations
                  </h4>
                  <CacheStatusIndicator status={cacheStatus.destinations} itemCount={allDestinations.length} />
                </div>
                <button
                  type="button"
                  onClick={refreshDestinations}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 text-xs font-medium shadow-md hover:shadow-lg"
                  title="Refresh destinations"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>🔄 Refresh</span>
                </button>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scroll">
                {Array.isArray(allDestinations) && allDestinations.length > 0 ? (
                  allDestinations.map(dest => (
                  <div key={dest.id} className="p-3 border-2 border-blue-100 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                    <label className="flex items-center mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isLocationSelected(dest.id, 'destination')}
                        onChange={() => handleLocationChange('destination', dest.id)}
                        className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="ml-3 font-medium text-gray-800">{dest.name}</span>
                    </label>

                    {/* Sub-destinations */}
                    {dest.sub_destinations && dest.sub_destinations.length > 0 && (
                      <div className="ml-6 space-y-2 mt-2 border-t border-blue-100 pt-2">
                        {dest.sub_destinations.map(sub => (
                          <label key={sub.id} className="flex items-center cursor-pointer hover:bg-blue-50 p-1 rounded transition-colors duration-200">
                            <input
                              type="checkbox"
                              checked={isLocationSelected(sub.id, 'sub_destination')}
                              onChange={() => handleLocationChange('sub_destination', sub.id, dest.id)}
                              className="form-checkbox h-3 w-3 text-blue-500 rounded focus:ring-2 focus:ring-blue-400"
                            />
                            <span className="ml-2 text-sm text-gray-700">{sub.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-sm">No destinations available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Inclusions */}
            <div className="border-2 border-green-200 rounded-xl p-5 bg-gradient-to-br from-green-50 to-emerald-50 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-3">
                  <h4 className="text-md font-semibold text-gray-800 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    Inclusions
                  </h4>
                  <CacheStatusIndicator status={cacheStatus.inclusions} itemCount={inclusionOptions.length} />
                </div>
                <button
                  type="button"
                  onClick={refreshInclusions}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 text-xs font-medium shadow-md hover:shadow-lg"
                  title="Refresh inclusions"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>🔄 Refresh</span>
                </button>
              </div>
              
              {/* Inclusions Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={inclusionSearchTerm}
                    onChange={(e) => setInclusionSearchTerm(e.target.value)}
                    placeholder="Search inclusions..."
                    className="w-full pl-10 pr-4 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-sm"
                  />
                  {inclusionSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setInclusionSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {inclusionSearchTerm && (
                  <p className="text-xs text-green-600 mt-1">
                    {filteredInclusions.length} of {inclusionOptions.length} inclusions
                  </p>
                )}
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto pr-2 custom-scroll">
                {Array.isArray(filteredInclusions) && filteredInclusions.length > 0 ? (
                  filteredInclusions.map(inclusion => {
                    const isChecked = formData.including.includes(inclusion.id);
                    return (
                  <label key={inclusion.id} className="flex items-center cursor-pointer p-2 bg-white rounded-lg hover:bg-green-50 transition-colors duration-200 border border-green-100 shadow-sm">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleInclusionChange(inclusion)}
                      className="form-checkbox h-4 w-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">{inclusion.inclusion_text}</span>
                  </label>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-sm">
                      {inclusionSearchTerm 
                        ? `No inclusions found for "${inclusionSearchTerm}"`
                        : "No inclusions available"
                      }
                    </p>
                    {inclusionSearchTerm && (
                      <button
                        type="button"
                        onClick={() => setInclusionSearchTerm('')}
                        className="text-green-600 hover:text-green-700 text-sm mt-2 underline"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Exclusions */}
            <div className="border-2 border-red-200 rounded-xl p-5 bg-gradient-to-br from-red-50 to-pink-50 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-3">
                  <h4 className="text-md font-semibold text-gray-800 flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                    Exclusions
                  </h4>
                  <CacheStatusIndicator status={cacheStatus.exclusions} itemCount={exclusionOptions.length} />
                </div>
                <button
                  type="button"
                  onClick={refreshExclusions}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 text-xs font-medium shadow-md hover:shadow-lg"
                  title="Refresh exclusions"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>🔄 Refresh</span>
                </button>
              </div>
              
              {/* Exclusions Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={exclusionSearchTerm}
                    onChange={(e) => setExclusionSearchTerm(e.target.value)}
                    placeholder="Search exclusions..."
                    className="w-full pl-10 pr-4 py-2 border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white text-sm"
                  />
                  {exclusionSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setExclusionSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {exclusionSearchTerm && (
                  <p className="text-xs text-red-600 mt-1">
                    {filteredExclusions.length} of {exclusionOptions.length} exclusions
                  </p>
                )}
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto pr-2 custom-scroll">
                {Array.isArray(filteredExclusions) && filteredExclusions.length > 0 ? (
                  filteredExclusions.map(exclusion => {
                    const isChecked = formData.excluding.includes(exclusion.id);
                    return (
                  <label key={exclusion.id} className="flex items-center cursor-pointer p-2 bg-white rounded-lg hover:bg-red-50 transition-colors duration-200 border border-red-100 shadow-sm">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleExclusionChange(exclusion)}
                      className="form-checkbox h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">{exclusion.exclusion_text}</span>
                  </label>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-sm">
                      {exclusionSearchTerm 
                        ? `No exclusions found for "${exclusionSearchTerm}"`
                        : "No exclusions available"
                      }
                    </p>
                    {exclusionSearchTerm && (
                      <button
                        type="button"
                        onClick={() => setExclusionSearchTerm('')}
                        className="text-red-600 hover:text-red-700 text-sm mt-2 underline"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Row 8: SEO */}
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <div className="w-3 h-3 bg-violet-500 rounded-full mr-3"></div>
            SEO Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Input 
                label="Meta Title" 
                id="meta_title" 
                name="meta_title" 
                value={formData.meta_title} 
                onChange={handleInputChange} 
                className={`border-violet-200 focus:border-violet-500 focus:ring-violet-500 ${validationErrors.meta_title ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="SEO meta title (max 60 chars)"
              />
              <ValidationMessage error={validationErrors.meta_title} />
              {formData.meta_title && formData.meta_title.length <= 60 && formData.meta_title.length > 0 && (
                <ValidationMessage error={`Good SEO length (${formData.meta_title.length}/60 chars)`} type="success" />
              )}
              {formData.meta_title && formData.meta_title.length > 50 && formData.meta_title.length <= 60 && (
                <ValidationMessage error={`${60 - formData.meta_title.length} characters left for SEO`} type="warning" />
              )}
            </div>
            <div>
              <Input 
                label="Meta Description" 
                id="meta_description" 
                name="meta_description" 
                value={formData.meta_description} 
                onChange={handleInputChange} 
                className={`border-violet-200 focus:border-violet-500 focus:ring-violet-500 ${validationErrors.meta_description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="SEO meta description (max 160 chars)"
              />
              <ValidationMessage error={validationErrors.meta_description} />
              {formData.meta_description && formData.meta_description.length <= 160 && formData.meta_description.length > 0 && (
                <ValidationMessage error={`Good SEO length (${formData.meta_description.length}/160 chars)`} type="success" />
              )}
              {formData.meta_description && formData.meta_description.length > 140 && formData.meta_description.length <= 160 && (
                <ValidationMessage error={`${160 - formData.meta_description.length} characters left for SEO`} type="warning" />
              )}
            </div>
            <div>
              <Input 
                label="Meta Keywords" 
                id="meta_keywords" 
                name="meta_keywords" 
                value={formData.meta_keywords} 
                onChange={handleInputChange} 
                className="border-violet-200 focus:border-violet-500 focus:ring-violet-500"
                placeholder="Comma-separated keywords"
              />
              {formData.meta_keywords && formData.meta_keywords.split(',').length > 0 && (
                <ValidationMessage error={`${formData.meta_keywords.split(',').length} keywords added`} type="success" />
              )}
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-between items-center space-x-4 pt-6 border-t-2 border-gray-200">
          <div className="flex items-center space-x-4">
            {/* Form validity indicator */}
            <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
              isFormValid 
                ? 'bg-green-100 text-green-700 border border-green-200' 
                : 'bg-amber-100 text-amber-700 border border-amber-200'
            }`}>
              {isFormValid ? (
                <span className="flex items-center">
                  <span className="mr-2">✅</span>
                  Form Ready to Submit
                </span>
              ) : (
                <span className="flex items-center">
                  <span className="mr-2">📝</span>
                  Complete Required Fields
                </span>
              )}
            </div>
            
            {/* Validation summary */}
            <div className="text-xs text-gray-500">
              {!formData.title || formData.title.trim().length < 3 ? '• Title required ' : ''}
              {!formData.description || formData.description.trim().length < 20 ? '• Description required ' : ''}
              {formData.days !== formData.nights + 1 ? '• Fix duration ' : ''}
              {(!formData.images || formData.images.length < 6) ? '• Need 6+ images ' : ''}
            </div>
          </div>
          
          <div className="flex space-x-4">
            <Button 
              type="button" 
              onClick={() => navigate('/packages')} 
              variant="outline" 
              className="py-3 px-6 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200"
            >
              Back to Packages
            </Button>
            <Button type="button" onClick={() => navigate('/packages')} variant="secondary" className="py-3 px-6 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200">
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="success" 
              disabled={loading || !isFormValid}
              className={`py-3 px-6 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200 ${
                !isFormValid || loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Processing...' : (currentPackageId ? 'Update Package' : 'Create Package')}
            </Button>
          </div>
        </div>
      </form>
      </div>
    </div>
  );
}

export default PackageFormPage;